import { MenuOutlined } from '@ant-design/icons';
import ProLayout, { type BasicLayoutProps } from '@ant-design/pro-layout';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAccount } from '../../components/contexts/AccountContextManager';
import Footer from '../../components/Footer';
import PortalLoadingElement from '../../components/PortalLoadingElement';
import breakpoints from '../../utilities/breakpoints';
import { LogoContainer } from '../BasicLayout/styled';
import ErrorBoundary from '../ErrorBoundary';
import HeaderLogo from '../HeaderLogo';
import BasicProRightContent from './BasicProRightContent';
import CIMenu from './CIMenu';
import useBasicProLayout from './useBasicProLayout';
import UserSelfDrawer from './UserSelfDrawer';

export type BasicProLayoutProps = {
    children: JSX.Element;
};

const itemRender = route => (
    <Link key={route.key} to={route.path}>
        {route.breadcrumbName}
    </Link>
);

const footerRender = () => <Footer />;

const StyledProLayout = styled(ProLayout)`
    .ant-layout {
        background-color: #ffffff;
    }

    .ant-pro-global-header {
        padding: 0 24px;
    }

    & .ant-layout-content {
        margin-bottom: 0;
    }

    ${LogoContainer} {
        margin: auto;
        height: auto;
        width: 200px;

        @media screen and (max-width: 999px) {
            position: relative;
            margin: 0px;
            width: fit-content;
            margin-left: 15px;
        }
    }
`;

const Button = styled.button`
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
`;

const MenuButtonContainer = styled.div`
    display: flex;
    height: 48px;
    margin-top: 1px; /* the menu icon itself not totally vertical aligned, add 1px margin to fix the alignment  */

    @media screen and (min-width: ${breakpoints.md}) {
        margin-left: 36px;
    }
`;

const headerContentRender = () => <HeaderLogo link="/" />;

const rightContentRender = () => <BasicProRightContent />;

const BasicProLayout = ({ children }: BasicProLayoutProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAccount(true);

    const { layout, menuItems } = useBasicProLayout();

    const [collapsed, setCollapsed] = useState(true);
    const expandMenu = useCallback(() => {
        setCollapsed(false);
    }, [setCollapsed]);
    const collapseMenu = useCallback(() => {
        setCollapsed(true);
    }, [setCollapsed]);

    const [userSelfDrawerCollapsed, setUserSelfDrawerCollapsed] = useState(true);
    const expandUserSelfDrawer = useCallback(() => {
        setUserSelfDrawerCollapsed(false);
    }, [setUserSelfDrawerCollapsed]);
    const collapseUserSelfDrawer = useCallback(() => {
        setUserSelfDrawerCollapsed(true);
    }, [setUserSelfDrawerCollapsed]);

    const onMenuHeaderClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
    }, []);

    const onShowMenuClicked = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            expandMenu();
        },
        [expandMenu]
    );

    const headerTitleRender = useCallback(
        () => (
            <MenuButtonContainer>
                <Button onClick={onShowMenuClicked} style={{ cursor: 'pointer' }} type="button">
                    <MenuOutlined style={{ color: 'var(--ant-primary-color)' }} />
                </Button>
            </MenuButtonContainer>
        ),
        [onShowMenuClicked]
    );

    const onMenuItemClicked = useCallback(
        (path: string) => {
            navigate(path);
            collapseMenu();
        },
        [collapseMenu, navigate]
    );

    const handleAvatarClicked = useCallback(() => {
        collapseMenu();
        expandUserSelfDrawer();
    }, [collapseMenu, expandUserSelfDrawer]);

    const menuRender = useCallback<Exclude<BasicLayoutProps['menuRender'], undefined | false>>(
        () => (
            <CIMenu
                collapsed={collapsed}
                menuItems={menuItems}
                onAvatarClicked={handleAvatarClicked}
                onClose={collapseMenu}
                onMenuItemClicked={onMenuItemClicked}
            />
        ),
        [collapsed, collapseMenu, onMenuItemClicked, handleAvatarClicked]
    );

    const userSelfDrawer = useMemo(
        () => <UserSelfDrawer collapsed={userSelfDrawerCollapsed} onClose={collapseUserSelfDrawer} />,
        [userSelfDrawerCollapsed, collapseUserSelfDrawer]
    );

    return (
        <>
            <StyledProLayout
                footerRender={footerRender}
                headerContentRender={headerContentRender}
                headerHeight={80}
                headerTheme="light"
                headerTitleRender={user ? headerTitleRender : false}
                itemRender={itemRender}
                layout="mix"
                location={location}
                logo={null}
                menuRender={menuRender}
                navTheme={layout.navigationTheme as BasicLayoutProps['navTheme']}
                onMenuHeaderClick={onMenuHeaderClick}
                rightContentRender={rightContentRender}
                title={false}
                disableMobile
            >
                <Suspense fallback={<PortalLoadingElement />}>
                    <ErrorBoundary>{children}</ErrorBoundary>
                </Suspense>
            </StyledProLayout>
            {userSelfDrawer}
        </>
    );
};

export default BasicProLayout;
